import React from "react";
import { CompanyCard } from "../../../common/u1/CompanyCard";
import { ResultsFootActions } from "../SearchNotFoundActions";
import { Button } from "../../../common/u1/Button";

/* Redesigned results list — SERVER-side pagination. */
export const SearchResults = ({
  data = [],
  page = 1,
  setPage = () => {},
  totalPages = 1,
}) => {
  const currentPage = Math.max(1, page);

  const goPage = (n) => {
    const next = Math.min(Math.max(1, n), totalPages);
    if (next === currentPage) return;
    setPage(next);
    window.scrollTo({ top: 150, behavior: "smooth" });
  };

  // Truncated page list: 1 2 3 … 10 rather than a long row of buttons.
  const pageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const nums = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    const sorted = [...nums].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push(`gap-${n}`);
      out.push(n);
    });
    return out;
  };

  return (
    <div>
      <div className="grid gap-3">
        {data.map((company, i) => (
          <CompanyCard
            key={company?.companyID || company?.wb_win || `result-${currentPage}-${i}`}
            company={company}
          />
        ))}
      </div>

      {/* TIP-556 — the user has seen every result and still might not have
          found their company: offer claim / order-web-profile on the last page. */}
      {data.length > 0 && currentPage === totalPages && <ResultsFootActions />}

      {totalPages > 1 && (
        <nav
          aria-label="Search results pagination"
          className="flex items-center justify-between gap-3 mt-5 flex-wrap"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => goPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="flex-1 sm:flex-none min-h-[44px] sm:min-h-0"
          >
            Previous
          </Button>

          <ul className="flex gap-1.5 flex-wrap justify-center list-none m-0 p-0 order-first sm:order-none w-full sm:w-auto">
            {pageNumbers().map((n) =>
              typeof n === "string" ? (
                <li
                  key={n}
                  aria-hidden="true"
                  className="min-w-[34px] h-[34px] flex items-center justify-center text-[13px] text-[var(--muted)]"
                >
                  …
                </li>
              ) : (
                <li key={n}>
                  <button
                    type="button"
                    onClick={() => goPage(n)}
                    aria-current={n === currentPage ? "page" : undefined}
                    aria-label={`Go to page ${n}`}
                    className={`min-w-[34px] h-[34px] px-2 rounded-[9px] border text-[13px] font-semibold font-sans transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--orange)] focus-visible:ring-offset-1 ${
                      n === currentPage
                        ? "border-[var(--orange)] bg-[var(--orange-soft)] text-[var(--orange)]"
                        : "border-[var(--line)] bg-white text-[var(--ink-2)] hover:border-[var(--line-2)]"
                    }`}
                  >
                    {n}
                  </button>
                </li>
              )
            )}
          </ul>

          <Button
            variant="outline"
            size="sm"
            onClick={() => goPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="flex-1 sm:flex-none min-h-[44px] sm:min-h-0"
          >
            Next
          </Button>
        </nav>
      )}
    </div>
  );
};

export default SearchResults;
